/* eslint-disable no-underscore-dangle */
import { DTN_AUTH_API } from 'configs/endpoints';
import { DTN_CLIENT_ID, DTN_CLIENT_SECRET } from 'configs/credentials';

interface TokenResponse {
  data: {
    access_token: string;
    expires_in: number;
    token_type: string;
  };
}

const REFRESH_MARGIN_MS = 10_000; // refresh 10s before expiry
const AUDIENCE = 'https://map.api.dtn.com';

class DTNTokenManager {
  private token: string | null = null;

  private expiresAt: number = 0;

  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  private inflight: Promise<string> | null = null;

  private async fetchToken(): Promise<string> {
    const response = await fetch(`${DTN_AUTH_API}/tokens/authorize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        grant_type: 'client_credentials',
        client_id: DTN_CLIENT_ID,
        client_secret: DTN_CLIENT_SECRET,
        audience: AUDIENCE,
      }),
    });

    if (!response.ok) {
      throw new Error(`DTN auth failed: ${response.status} ${response.statusText}`);
    }

    const json: TokenResponse = await response.json();
    this.token = json.data.access_token;
    this.expiresAt = Date.now() + json.data.expires_in * 1000;

    this.scheduleRefresh(json.data.expires_in * 1000);

    return this.token;
  }

  private scheduleRefresh(expiresInMs: number) {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    const delay = Math.max(expiresInMs - REFRESH_MARGIN_MS, 0);
    this.refreshTimer = setTimeout(() => {
      this.inflight = this.fetchToken().catch(() => {
        // silent — next getToken() call will retry
        this.inflight = null;
        return this.token ?? '';
      });
    }, delay);
  }

  /** Async — guarantees a valid token, fetching one if needed. */
  async getToken(): Promise<string> {
    if (this.token && Date.now() < this.expiresAt - REFRESH_MARGIN_MS) {
      return this.token;
    }
    if (this.inflight) return this.inflight;
    this.inflight = this.fetchToken().finally(() => { this.inflight = null; });
    return this.inflight;
  }

  /**
   * Sync — returns the last known token (may be stale).
   * Safe to use inside tileUrlFunction since getToken() proactively refreshes.
   */
  getCachedToken(): string {
    return this.token ?? '';
  }

  isInitialized(): boolean {
    return this.token !== null;
  }
}

// eslint-disable-next-line import/prefer-default-export
export const dtnTokenManager = new DTNTokenManager();
